import React, { useState } from 'react';
import { Elements, useStripe, useElements, CardNumberElement, CardExpiryElement, CardCvcElement } from '@stripe/react-stripe-js';
import { X, CreditCard, Calendar, Lock, User } from 'lucide-react';
import toast from 'react-hot-toast';

const StripeForm = ({ amount, clientSecret, onSuccess, onClose }) => {
    const stripe = useStripe();
    const elements = useElements();
    const [isProcessing, setIsProcessing] = useState(false);
    const [cardHolderName, setCardHolderName] = useState('');

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!stripe || !elements) return;

        if (!cardHolderName.trim()) {
            toast.error("Please enter cardholder name");
            return;
        }

        setIsProcessing(true);

        const cardNumberElement = elements.getElement(CardNumberElement);

        const { error, paymentIntent } = await stripe.confirmCardPayment(clientSecret, {
            payment_method: {
                card: cardNumberElement,
                billing_details: {
                    name: cardHolderName,
                },
            },
        });

        if (error) {
            toast.error(error.message);
            setIsProcessing(false);
        } else if (paymentIntent.status === 'succeeded') {
            toast.success("Payment successful!");
            onClose();
            onSuccess(paymentIntent);
            setIsProcessing(false);
        }
    };

    return (
        <form onSubmit={handleSubmit} className="space-y-5">
            <div className="space-y-1">
                <label className="text-[10px] font-cyber font-medium text-gray-500 uppercase tracking-widest">Cardholder Name</label>
                <div className="relative">
                    <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-600" />
                    <input
                        type="text"
                        placeholder="John Doe"
                        value={cardHolderName}
                        onChange={(e) => setCardHolderName(e.target.value)}
                        className="w-full pl-10 pr-3 py-2.5 rounded-xl text-xs font-cyber text-white placeholder-gray-600 bg-white/[0.04] border border-white/10 focus:border-cyber-purple/50 focus:outline-none transition-all"
                    />
                </div>
            </div>

            <div className="space-y-1">
                <label className="text-[10px] font-cyber font-medium text-gray-500 uppercase tracking-widest">Card Number</label>
                <div className="relative">
                    <CreditCard className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-600" />
                    <div className="w-full pl-10 pr-3 py-2.5 rounded-xl bg-white/[0.04] border border-white/10 focus-within:border-cyber-purple/50 transition-all">
                        <CardNumberElement options={{
                            style: {
                                base: {
                                    color: '#fff',
                                    fontFamily: 'monospace',
                                    fontSize: '14px',
                                    '::placeholder': { color: '#4b5563' },
                                },
                            },
                        }} />
                    </div>
                </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1">
                    <label className="text-[10px] font-cyber font-medium text-gray-500 uppercase tracking-widest">Expiry Date</label>
                    <div className="relative">
                        <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-600" />
                        <div className="w-full pl-10 pr-3 py-2.5 rounded-xl bg-white/[0.04] border border-white/10 focus-within:border-cyber-purple/50 transition-all">
                            <CardExpiryElement options={{
                                style: {
                                    base: {
                                        color: '#fff',
                                        fontFamily: 'monospace',
                                        fontSize: '14px',
                                        '::placeholder': { color: '#4b5563' },
                                    },
                                },
                            }} />
                        </div>
                    </div>
                </div>
                <div className="space-y-1">
                    <label className="text-[10px] font-cyber font-medium text-gray-500 uppercase tracking-widest">CVV</label>
                    <div className="relative">
                        <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-600" />
                        <div className="w-full pl-10 pr-3 py-2.5 rounded-xl bg-white/[0.04] border border-white/10 focus-within:border-cyber-purple/50 transition-all">
                            <CardCvcElement options={{
                                style: {
                                    base: {
                                        color: '#fff',
                                        fontFamily: 'monospace',
                                        fontSize: '14px',
                                        '::placeholder': { color: '#4b5563' },
                                    },
                                },
                            }} />
                        </div>
                    </div>
                </div>
            </div>

            <button
                type="submit"
                disabled={isProcessing || !stripe}
                className="cyber-btn w-full !py-4 flex items-center justify-center gap-2 disabled:opacity-50 disabled:pointer-events-none"
            >
                {isProcessing ? 'PROCESSING...' : `PAY $${amount.toFixed(2)}`}
            </button>
        </form>
    );
};

const StripePaymentModal = ({ isOpen, onClose, amount, clientSecret, stripePromise, onSuccess }) => {
    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-50 bg-[#04040a]/65 backdrop-blur-md flex items-center justify-center p-4">
            <div className="relative w-full max-w-md rounded-2xl border border-white/10 bg-[#0c0c18] p-6 space-y-5 shadow-[0_0_50px_rgba(0,0,0,0.8)] overflow-hidden">
                <div className="absolute top-0 left-0 w-full h-[2px] bg-gradient-to-r from-cyber-blue via-cyber-cyan to-cyber-purple"></div>

                <div className="flex items-start justify-between">
                    <div>
                        <h3 className="font-cyber font-bold text-lg text-white uppercase tracking-wider flex items-center gap-2">
                            <CreditCard className="w-5 h-5 text-cyber-purple" />
                            Stripe Payment
                        </h3>
                        <p className="text-gray-500 text-xs font-sans mt-1">
                            Enter your card details to complete the purchase.
                        </p>
                    </div>
                    <button
                        onClick={onClose}
                        className="p-1.5 rounded-lg bg-white/5 border border-white/10 hover:border-cyber-pink/40 hover:bg-cyber-pink/10 text-gray-400 hover:text-cyber-pink transition-all"
                    >
                        <X className="w-4 h-4" />
                    </button>
                </div>

                <Elements stripe={stripePromise}>
                    <StripeForm amount={amount} clientSecret={clientSecret} onSuccess={onSuccess} onClose={onClose} />
                </Elements>
            </div>
        </div>
    );
};

export default StripePaymentModal;
